import { chroma } from '@methanesat/maps';
import { BarGradient, Box, buildLinearGradient, Button, Typography, useTheme } from '@methanesat/ui-components';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ColorScales } from '../../consts';
import { useTranslate } from '../../hooks';
import { selectL4MSATColorScale } from '../../reducers';
import type { RASTER_COLORMAP_NAMES } from '../../types';
import { DispersedEmissionsTooltip } from '../EmissionsMapLegend/MiniLegend/DispersedEmissionsTooltip';
import { getBarGradientWidth } from '../EmissionsMapLegend/MiniLegend/MiniLegendUtils';

export interface ColorScalePickerProps {
    height?: string; // used to scale and align thermometer with selection boxes
    colorScales: ColorScales;
    onColorScaleChange: (scale: RASTER_COLORMAP_NAMES) => void;
    disabled: boolean;
}

const formatScaleName = (scale: string): string => {
    return scale.replace(/_/g, '-');
};
const ColorScalePicker = ({ colorScales, height = '140px', onColorScaleChange, disabled }: ColorScalePickerProps) => {
    const t = useTranslate();
    const theme = useTheme();

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const labelHeightPx = 12;
    const canvasHeight = parseFloat(height);
    const boxHeight = canvasHeight + 10;

    const selectedColorScale = useSelector(selectL4MSATColorScale);

    // Drawing canvas when selection changes
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { colors } = colorScales[selectedColorScale];

        if (ctx) {
            canvas.height = canvasHeight - 3;
            canvas.width = 13;

            const colorScale = chroma
                .scale(colors.map((c) => c.color))
                .mode('lch')
                .domain(colors.map((c) => (c.percent / 100) * canvasHeight));

            for (let i = 0; i < canvas.height; i++) {
                ctx.fillStyle = colorScale(i).css();
                ctx.fillRect(0, canvas.height - i, canvas.width, 1);
            }
        }
    }, [selectedColorScale, colorScales, canvasHeight]);

    return (
        <Box data-testid="color-scale-picker" display="flex" flexDirection="column" gap={1} padding={1}>
            <Box
                data-testid="color-scale-picker-label"
                display="flex"
                flexDirection="row"
                alignItems="start"
                justifyContent="space-between"
            >
                <Typography variant="body2" fontWeight={theme.typography.fontWeightBold}>
                    {t(`emissionsMapPage.units.kgHrKm2`)}
                </Typography>
            </Box>

            <Box data-testid="color-thermometers" display="flex" flexDirection="row" alignItems="flex-end" gap={1}>
                {/* Thermometer canvas - changes depending on scale selected */}
                <Box
                    data-testid="color-thermometer-canvas"
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    sx={{
                        height: boxHeight
                    }}
                >
                    <canvas ref={canvasRef} role="presentation" />
                </Box>

                {/* Titles and sets of ticks & labels - specific to each scale */}
                <Box display="flex" flexDirection="row" alignItems="flex-end">
                    {Object.keys(colorScales).map((scale) => {
                        const { colors, labels } = colorScales[scale];
                        const colorsArray = colors.map((c) => c.color);
                        const gradientString = buildLinearGradient(colorsArray);

                        const isSelected = selectedColorScale === scale;

                        return (
                            <Button
                                data-testid={`color-thermometer-${formatScaleName(scale)}`}
                                key={scale}
                                disabled={disabled}
                                sx={{
                                    role: 'button',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    minWidth: '2.5em', // prevents color scale collapsing smaller than labels
                                    maxWidth: '100%'
                                }}
                                onClick={() => onColorScaleChange(scale as RASTER_COLORMAP_NAMES)}
                            >
                                {/* Title and horizontal gradient */}
                                <Box marginBottom="2px" width="2.5em">
                                    <Typography
                                        fontWeight={theme.typography.fontWeightBold}
                                        fontSize={10}
                                        sx={{ color: disabled ? theme.palette.disabledGray.main : 'inherit' }}
                                    >
                                        {t(
                                            `emissionsMapPage.miniLegend.area-emission-raster.l4ColorScales.${scale}.title`
                                        )}
                                    </Typography>
                                    <Box width={getBarGradientWidth(scale)}>
                                        <BarGradient marginTop="0px" height="3px" gradientString={gradientString} />
                                    </Box>
                                </Box>

                                {/* Selectable box */}
                                <Box
                                    data-testid={`color-thermometer-${formatScaleName(scale)}-box`}
                                    sx={{
                                        border: 'solid',
                                        borderWidth: isSelected ? '2px' : '0.7px',
                                        borderRadius: theme.shape.borderRadius,
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: disabled
                                            ? theme.palette.action.disabledBackground
                                            : theme.palette.secondary.main,
                                        color: disabled ? theme.palette.disabledGray.main : 'inherit',
                                        opacity: disabled ? 0.75 : 1,
                                        height: boxHeight,
                                        width: '100%',
                                        position: 'relative',
                                        padding: isSelected ? '3.7px' : '5px', // Adjust padding for selected state

                                        boxSizing: 'border-box',
                                        pointerEvents: disabled ? 'none' : 'auto'
                                    }}
                                >
                                    <Box display="flex" position="relative" flex="1" height="100%" width="100%">
                                        {labels.map((labelItem, index) => {
                                            const { label, percent } = labelItem;

                                            const isTopLabel = index === 0;
                                            const isBottomLabel = index === labels.length - 1;
                                            const isBold = isBottomLabel || isTopLabel;

                                            const relativePosition = `calc(${100 - percent}% - ${labelHeightPx / 2}px)`;

                                            // keeps bottom label inside the box
                                            // returns the smallest value between the accurately centered and maximum safe label positioning
                                            const safePosition = isBottomLabel
                                                ? `min(${relativePosition}, calc(${100}% - ${labelHeightPx}px))`
                                                : relativePosition;

                                            return (
                                                <Box
                                                    top={safePosition}
                                                    paddingLeft="2px"
                                                    width="100%"
                                                    position="absolute"
                                                    key={percent}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        height: '20px',
                                                        /** Tick marks */
                                                        '&::before': {
                                                            content: '"—"',
                                                            fontSize: '0.4rem',
                                                            position: 'absolute',
                                                            left: '-6px'
                                                        }
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={
                                                            isBold
                                                                ? theme.typography.fontWeightBold
                                                                : theme.typography.fontWeightRegular
                                                        }
                                                    >
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Button>
                        );
                    })}
                    <DispersedEmissionsTooltip colorScales={colorScales} />
                </Box>
            </Box>
        </Box>
    );
};

export default ColorScalePicker;
